// Promo, creator, and utm values that must survive landing, signup, options, and checkout.
// Empty later pages keep the earlier values. A new ?promo= replaces the stored code.
// Last touch wins: a new creator replaces the stored one, and a new utm set
// replaces the whole stored set so sources and campaigns never mix.

import { normalizeCreatorCode } from "./marketingAttribution";

export const PAYSTEP_PARAMS_KEY = "dople_paystep_params_v1";

const PROMO = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"];
const UTM_MAX = 100;
const CLICK_MAX = 255;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"];
const PROMO_KEYS = ["promo", "promotion_code", "promo_code"];

export function normalizePromo(value) {
  const code = String(value || "").trim();
  return PROMO.test(code) ? code : "";
}

function defaultStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function creatorFromPath(pathname) {
  const match = String(pathname || "").match(/^\/(?:r|c)\/([^/]+)\/?$/);
  if (!match) return "";
  try {
    return normalizeCreatorCode(decodeURIComponent(match[1]));
  } catch {
    return normalizeCreatorCode(match[1]);
  }
}

export function observePaystepSearch({ search = "", pathname = "" } = {}) {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const observed = { utm: {} };
  let promo = "";
  PROMO_KEYS.forEach((key) => {
    if (!promo) promo = normalizePromo(params.get(key));
  });
  if (promo) observed.promo = promo;
  const creator = normalizeCreatorCode(
    params.get("ref") || params.get("creator") || params.get("creator_code") || ""
  ) || creatorFromPath(pathname);
  if (creator) observed.creator_code = creator;
  const optionId = String(params.get("option") || "").trim();
  if (optionId) observed.optionId = optionId.slice(0, 255);
  params.forEach((value, rawKey) => {
    const key = String(rawKey || "").trim().toLowerCase();
    const text = String(value || "").trim();
    if (!text) return;
    if (UTM_KEYS.includes(key)) observed.utm[key] = text.slice(0, UTM_MAX);
    else if (CLICK_KEYS.includes(key)) observed.utm[key] = text.slice(0, CLICK_MAX);
  });
  return observed;
}

export function mergePaystepParams(stored, observed) {
  const current = stored && typeof stored === "object" ? stored : {};
  const next = { ...current, utm: { ...(current.utm || {}) } };
  const incoming = observed && typeof observed === "object" ? observed : {};
  if (incoming.promo) next.promo = incoming.promo;
  if (incoming.creator_code) next.creator_code = incoming.creator_code;
  const incomingUtm = incoming.utm && typeof incoming.utm === "object" ? incoming.utm : {};
  const freshUtm = {};
  Object.keys(incomingUtm).forEach((key) => {
    if (incomingUtm[key]) freshUtm[key] = incomingUtm[key];
  });
  if (Object.keys(freshUtm).length) next.utm = freshUtm;
  if (!Object.keys(next.utm).length) delete next.utm;
  return next;
}

export function readPaystepParams(storage = defaultStorage()) {
  if (!storage) return {};
  try {
    const raw = storage.getItem(PAYSTEP_PARAMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePaystepParams(storage, value) {
  if (!storage) return;
  try {
    storage.setItem(PAYSTEP_PARAMS_KEY, JSON.stringify(value));
  } catch {
    // Storage can be blocked. The checkout request still sends whatever is in memory.
  }
}

export function persistPaystepSearch(location, storage = defaultStorage()) {
  const search = location && location.search ? location.search : "";
  const pathname = location && location.pathname ? location.pathname : "";
  const next = mergePaystepParams(readPaystepParams(storage), observePaystepSearch({ search, pathname }));
  writePaystepParams(storage, next);
  return next;
}

export function rememberCheckoutSelection(selection, storage = defaultStorage()) {
  const current = readPaystepParams(storage);
  const next = mergePaystepParams(current, {
    promo: selection && selection.promo ? selection.promo : "",
  });
  if (selection && selection.tripId) next.tripId = String(selection.tripId);
  if (selection && selection.optionId) next.optionId = String(selection.optionId);
  if (selection && Object.prototype.hasOwnProperty.call(selection, "promo")) {
    const normalized = normalizePromo(selection.promo);
    if (normalized) next.promo = normalized;
    else delete next.promo;
  }
  writePaystepParams(storage, next);
  return next;
}

export function attributionForCheckout(stored) {
  const source = stored && typeof stored === "object" ? stored : {};
  const attribution = {};
  if (source.creator_code) attribution.creator_code = source.creator_code;
  const utm = source.utm && typeof source.utm === "object" ? source.utm : {};
  Object.keys(utm).forEach((key) => {
    if (utm[key]) attribution[key] = utm[key];
  });
  return attribution;
}
